import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AppLogo from "../components/AppLogo";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";

/* ── Fonts ── */
if (typeof document !== "undefined" && !document.getElementById("__pfp_fonts")) {
  const l = document.createElement("link");
  l.id = "__pfp_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

const T = {
  green: "#1e9254", greenDark: "#155c36", greenMid: "#1b7a47",
  greenTint: "#eef8f3", greenBorder: "#a7d9bc",
  bg: "#F7F8FA", white: "#FFFFFF",
  border: "#E5E7EB", borderFaint: "#F0F2F5",
  text: "#0f1117", textMid: "#2d3549", sub: "#4a5268",
  faint: "#8a93a8", ghost: "#b0b8cc",
  serif: "'Instrument Serif', Georgia, serif",
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  tx: "all 0.20s cubic-bezier(.4,0,.2,1)",
};

const SIDEBAR_WIDTH = 268;
const RIGHT_WIDTH = 296;

const CATEGORIES = [
  "All", "Cleaning & Laundry", "Plumbing", "Electrical", "TV & Electronics",
  "Hair & Beauty", "Tutoring", "Photography", "Gardening", "Carpentry",
  "Moving", "Childcare", "Fitness", "Auto Repair", "Errands", "Personal Chef",
];

/* Major Kenyan towns — this drives the Location filter. Neighborhoods within
   Nairobi are included alongside major towns so the filter stays useful for
   both city-wide and Nairobi-local searches. This same "major town" concept
   (Nairobi, Mombasa, Kisumu, Nakuru, Eldoret, Thika, etc.) is what pros pick
   from in Settings → Account, so real data flows into these filters instead
   of everything defaulting to one city. */
const LOCATIONS = [
  "All Locations", "Nairobi", "Mombasa", "Kisumu", "Nakuru", "Eldoret",
  "Thika", "Westlands", "Kilimani", "Karen", "Lavington", "Upperhill", "South B",
];

const IMAGE_LINKS = [
  {
    img: "/images/jirani-guarantee.png",
    eyebrow: "Your protection",
    title: "The JiraniApp Guarantee",
    desc: "Every booking is backed. If something goes wrong, we make it right.",
    path: "/guarantee",
    cta: "Learn how we protect you",
    fallbackGradient: "linear-gradient(135deg,#eef8f3 0%,#a7d9bc 100%)",
    fallbackIcon: "🛡️",
  },
  {
    img: "/images/verified-pros.png",
    eyebrow: "Who you're booking",
    title: "Verified JiraniPros only",
    desc: "Every pro is identity-verified, background-checked, and reviewed by real clients.",
    path: "/find-a-jiranipro",
    cta: "Browse verified pros",
    fallbackGradient: "linear-gradient(135deg,#ede9fe 0%,#c4b5fd 100%)",
    fallbackIcon: "✅",
  },
  {
    img: "/images/profeed-business-pro.jpg",
    eyebrow: "Top tier professionals",
    title: "What is Business Pro?",
    desc: "Reserved for professionals with 100+ jobs and consistent 5-star ratings.",
    path: "/trust-and-safety",
    cta: "See how tiers work",
    fallbackGradient: "linear-gradient(135deg,#fef9c3 0%,#fde68a 100%)",
    fallbackIcon: "⭐",
  },
  {
    img: "/images/profeed-mpesa.jpg",
    eyebrow: "Safe payments",
    title: "Pay securely with M-Pesa",
    desc: "Payment is held safely and only released once your job is done.",
    path: "/how-it-works",
    cta: "How payments work",
    fallbackGradient: "linear-gradient(135deg,#dcfce7 0%,#86efac 100%)",
    fallbackIcon: "📱",
  },
  {
    img: "/images/profeed-become-a-pro.jpg",
    eyebrow: "Are you a professional?",
    title: "Join as a JiraniPro",
    desc: "Get discovered, build your reputation, and grow your business across Kenya.",
    path: "/become-a-pro",
    cta: "Start earning today",
    fallbackGradient: "linear-gradient(135deg,#dbeafe 0%,#93c5fd 100%)",
    fallbackIcon: "🚀",
  },
  {
    img: "/images/profeed-community.jpg",
    eyebrow: "For professionals",
    title: "Community Hub",
    desc: "Tips, events, and resources from JiraniApp's professional community.",
    path: "/community-hub",
    cta: "Join the community",
    fallbackGradient: "linear-gradient(135deg,#fce7f3 0%,#f9a8d4 100%)",
    fallbackIcon: "🤝",
  },
];

const TEXT_LINKS = [
  { label: "How JiraniApp works", path: "/how-it-works" },
  { label: "Trust & Safety", path: "/trust-and-safety" },
  { label: "Browse by location", path: "/browse-by-location" },
  { label: "Help & Support", path: "/help" },
  { label: "Customer reviews", path: "/customer-reviews" },
];

/* ── Icons ── */
const HomeIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const SearchIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
const VideoIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>;
const BookingsIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>;
const ProfileIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const SignInIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>;
const SignUpIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/></svg>;
const EyeIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const HeartIcon = ({ filled = false, size = 14 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "#ef4444" : "none"} stroke={filled ? "#ef4444" : "currentColor"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
const CommentIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
const ShareIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>;
const PinIcon = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.faint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const StarIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="#FBBF24" stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const ArrowRight = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>;
const ChevronDown = ({ open }) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: T.tx }}><path d="M6 9l6 6 6-6"/></svg>;
const ChevronUp = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>;
const ChevronDownNav = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>;
const LocationIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const CheckIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;

/* ── Tier Badge ── */
function TierBadge({ tier }) {
  const isBP = tier === "Business Pro";
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: isBP ? "rgba(255,251,235,0.95)" : "rgba(238,248,243,0.95)",
      border: `1px solid ${isBP ? "#FDE68A" : "#a7d9bc"}`,
      color: isBP ? "#92400E" : "#155c36",
      fontSize: 10, fontWeight: 700, padding: "3px 9px",
      borderRadius: 6, letterSpacing: ".03em", fontFamily: T.sans,
      backdropFilter: "blur(8px)",
    }}>
      {isBP ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="#D97706" stroke="none">
          <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="#1e9254" stroke="none">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
          <path d="M9 12l2 2 4-4" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
      {tier}
    </div>
  );
}

/* ── Collapsible sidebar section ── */
function CollapsibleSection({ title, icon, defaultOpen = true, children, maxHeight = 360 }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 4 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "9px 10px", borderRadius: 8, border: "none", background: "transparent",
          cursor: "pointer", fontFamily: T.sans, fontSize: 10.5, fontWeight: 700,
          color: T.ghost, textTransform: "uppercase", letterSpacing: ".09em",
          transition: T.tx,
        }}
        onMouseEnter={e => e.currentTarget.style.background = T.bg}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>{icon}{title}</span>
        <ChevronDown open={open} />
      </button>
      <div style={{
        maxHeight: open ? maxHeight : 0,
        overflow: open ? "auto" : "hidden",
        transition: "max-height 0.28s cubic-bezier(.4,0,.2,1)",
        /* custom slim scrollbar */
      }}>
        <style>{`.sidebar-scroll::-webkit-scrollbar{width:3px}.sidebar-scroll::-webkit-scrollbar-thumb{background:${T.border};border-radius:99px}`}</style>
        <div className="sidebar-scroll" style={{ overflowY: "auto", maxHeight: open ? maxHeight : 0 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ── Left Sidebar ── */
function LeftSidebar({ activeCategory, setActiveCategory, activeLocation, setActiveLocation, isMobile, user }) {
  const navigate = useNavigate();

  const navItems = [
    { icon: <HomeIcon/>, label: "Home", path: "/" },
    { icon: <SearchIcon/>, label: "Find a Pro", path: "/search" },
    { icon: <VideoIcon/>, label: "ProFeed", path: "/profeed", active: true },
    ...(user
      ? [
          { icon: <BookingsIcon/>, label: "My Bookings", path: "/bookings" },
          { icon: <ProfileIcon/>, label: "My Account", path: "/account" },
        ]
      : [
          { icon: <SignInIcon/>, label: "Sign In", path: "/login" },
          { icon: <SignUpIcon/>, label: "Sign Up", path: "/signup" },
        ]),
  ];

  if (isMobile) return null;

  return (
    <aside style={{
      width: SIDEBAR_WIDTH, flexShrink: 0,
      position: "sticky", top: 0, height: "100vh",
      overflowY: "auto", borderRight: `1px solid ${T.border}`,
      background: T.white, display: "flex", flexDirection: "column",
      padding: "0 0 24px",
    }}>
      <style>{`aside::-webkit-scrollbar{display:none}aside{-ms-overflow-style:none;scrollbar-width:none}`}</style>

      {/* Logo */}
      <div
        onClick={() => navigate("/")}
        style={{
          padding: "22px 20px 20px", cursor: "pointer",
          borderBottom: `1px solid ${T.borderFaint}`, marginBottom: 16,
          flexShrink: 0,
        }}
      >
        <AppLogo />
        <div style={{ fontSize: 10.5, color: T.faint, fontFamily: T.sans, marginTop: 3, fontWeight: 500, letterSpacing: ".04em" }}>ProFeed</div>
      </div>

      {/* Nav items */}
      <nav style={{ padding: "0 10px", marginBottom: 20, flexShrink: 0 }}>
        {navItems.map((item, i) => (
          <div key={i}
            onClick={() => navigate(item.path)}
            style={{
              display: "flex", alignItems: "center", gap: 11,
              padding: "10px 12px", borderRadius: 12, cursor: "pointer",
              marginBottom: 1,
              background: item.active ? T.greenTint : "transparent",
              color: item.active ? T.green : T.sub,
              fontFamily: T.sans, fontSize: 13.5, fontWeight: item.active ? 600 : 400,
              transition: T.tx,
            }}
            onMouseEnter={e => { if (!item.active) { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.text; } }}
            onMouseLeave={e => { if (!item.active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.sub; } }}
          >
            {item.icon}
            {item.label}
          </div>
        ))}
      </nav>

      {/* Divider */}
      <div style={{ height: 1, background: T.borderFaint, margin: "0 16px 16px", flexShrink: 0 }} />

      {/* Filters */}
      <div style={{ padding: "0 10px", flex: 1, overflowY: "auto" }}>

        {/* Location filter */}
        <CollapsibleSection
          title="Location"
          icon={<LocationIcon/>}
          defaultOpen={true}
          maxHeight={220}
        >
          <div style={{ padding: "2px 0 8px" }}>
            {LOCATIONS.map(loc => (
              <div key={loc}
                onClick={() => setActiveLocation(loc)}
                style={{
                  padding: "7px 10px", borderRadius: 8, cursor: "pointer",
                  fontSize: 12.5, fontFamily: T.sans,
                  fontWeight: activeLocation === loc ? 600 : 400,
                  color: activeLocation === loc ? T.green : T.sub,
                  background: activeLocation === loc ? T.greenTint : "transparent",
                  marginBottom: 1, transition: T.tx,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
                onMouseEnter={e => { if (activeLocation !== loc) { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.text; } }}
                onMouseLeave={e => { if (activeLocation !== loc) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.sub; } }}
              >
                {loc}
                {activeLocation === loc && <CheckIcon/>}
              </div>
            ))}
          </div>
        </CollapsibleSection>

        {/* Category filter */}
        <CollapsibleSection
          title="Category"
          icon={null}
          defaultOpen={true}
          maxHeight={320}
        >
          <div style={{ padding: "2px 0 8px" }}>
            {CATEGORIES.map(cat => (
              <div key={cat}
                onClick={() => setActiveCategory(cat)}
                style={{
                  padding: "7px 10px", borderRadius: 8, cursor: "pointer",
                  fontSize: 12.5, fontFamily: T.sans,
                  fontWeight: activeCategory === cat ? 600 : 400,
                  color: activeCategory === cat ? T.green : T.sub,
                  background: activeCategory === cat ? T.greenTint : "transparent",
                  marginBottom: 1, transition: T.tx,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}
                onMouseEnter={e => { if (activeCategory !== cat) { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.text; } }}
                onMouseLeave={e => { if (activeCategory !== cat) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.sub; } }}
              >
                {cat}
                {activeCategory === cat && <CheckIcon/>}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      </div>
    </aside>
  );
}

/* ── Right Sidebar ── */
function RightSidebar({ isMobile, onScrollFeed }) {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(true);

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 8);
    setCanScrollDown(el.scrollTop < el.scrollHeight - el.clientHeight - 8);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScroll, { passive: true });
    updateScroll();
    return () => el.removeEventListener("scroll", updateScroll);
  }, [updateScroll]);

  const scrollRight = (dir) => scrollRef.current?.scrollBy({ top: dir * 260, behavior: "smooth" });

  if (isMobile) return null;

  return (
    <aside style={{
      width: RIGHT_WIDTH, flexShrink: 0,
      position: "sticky", top: 0, height: "100vh",
      display: "flex", flexDirection: "column",
      borderLeft: `1px solid ${T.border}`,
      background: T.white,
    }}>
      {/* Scroll up button */}
      {canScrollUp && (
        <button onClick={() => scrollRight(-1)} style={{
          position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)",
          width: 32, height: 32, borderRadius: "50%",
          background: T.white, border: `1.5px solid ${T.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", zIndex: 5, boxShadow: "0 2px 8px rgba(0,0,0,.08)",
          transition: T.tx,
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.greenBorder; e.currentTarget.style.background = T.greenTint; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.white; }}
        >
          <ChevronUp/>
        </button>
      )}

      {/* Scrollable content */}
      <div ref={scrollRef} style={{
        flex: 1, overflowY: "auto", padding: "20px 18px 20px",
      }}>
        <style>{`.rs-scroll::-webkit-scrollbar{display:none}`}</style>

        <p style={{ fontSize: 10, fontWeight: 700, color: T.ghost, textTransform: "uppercase", letterSpacing: ".1em", margin: "0 0 14px", fontFamily: T.sans }}>
          From JiraniApp
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
          {IMAGE_LINKS.map((card, i) => (
            <RightImageCard key={i} card={card} onClick={() => navigate(card.path)}/>
          ))}
        </div>

        {/* Text links */}
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 18, marginBottom: 20 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: T.ghost, textTransform: "uppercase", letterSpacing: ".1em", margin: "0 0 10px", fontFamily: T.sans }}>
            Useful links
          </p>
          {TEXT_LINKS.map((link, i) => (
            <RightTextLink key={i} link={link} onClick={() => navigate(link.path)} last={i === TEXT_LINKS.length - 1}/>
          ))}
        </div>

        {/* Trust strip */}
        <div style={{
          padding: "16px", borderRadius: 16,
          background: "linear-gradient(135deg,#eef8f3 0%,#d1f0e0 100%)",
          border: `1px solid ${T.greenBorder}`,
        }}>
          <div style={{ fontFamily: T.serif, fontSize: 15, color: T.text, marginBottom: 5, fontStyle: "italic" }}>
            Trusted by <em style={{ color: T.green }}>50,000+</em> Kenyans
          </div>
          <p style={{ fontSize: 11.5, color: T.sub, lineHeight: 1.6, margin: "0 0 10px", fontFamily: T.sans }}>
            Every professional is verified. Every booking is protected. Pay only when the job is done.
          </p>
          <button onClick={() => navigate("/how-it-works")} style={{
            fontSize: 11.5, fontWeight: 600, color: T.green, background: "none",
            border: "none", cursor: "pointer", fontFamily: T.sans, padding: 0,
            display: "inline-flex", alignItems: "center", gap: 4,
          }}>
            See how it works <ArrowRight/>
          </button>
        </div>
      </div>

      {/* Scroll down button */}
      {canScrollDown && (
        <button onClick={() => scrollRight(1)} style={{
          position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)",
          width: 32, height: 32, borderRadius: "50%",
          background: T.white, border: `1.5px solid ${T.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", zIndex: 5, boxShadow: "0 -2px 8px rgba(0,0,0,.08)",
          transition: T.tx,
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.greenBorder; e.currentTarget.style.background = T.greenTint; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = T.white; }}
        >
          <ChevronDownNav/>
        </button>
      )}
    </aside>
  );
}

/* ── Right Image Card — premium fade overlay ── */
function RightImageCard({ card, onClick }) {
  const [h, setH] = useState(false);
  const [imgErr, setImgErr] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        borderRadius: 16, overflow: "hidden",
        border: `1.5px solid ${h ? T.greenBorder : T.border}`,
        background: T.white, cursor: "pointer",
        boxShadow: h ? "0 8px 28px rgba(30,146,84,.12)" : "0 1px 4px rgba(0,0,0,.05)",
        transition: T.tx,
      }}>

      {/* Image area with premium fade */}
      <div style={{ position: "relative", height: 96, overflow: "hidden", flexShrink: 0 }}>
        {!imgErr ? (
          <>
            <img src={card.img} alt={card.title}
              onError={() => setImgErr(true)}
              style={{
                width: "100%", height: "100%", objectFit: "cover", display: "block",
                transform: h ? "scale(1.05)" : "scale(1)",
                transition: "transform .5s cubic-bezier(.4,0,.2,1)",
                filter: h ? "brightness(0.92)" : "brightness(1)",
              }}
            />
            {/* Multi-stop fade: image fades down into white card content */}
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to bottom, transparent 0%, transparent 30%, rgba(255,255,255,0.15) 55%, rgba(255,255,255,0.72) 78%, rgba(255,255,255,1) 100%)",
              pointerEvents: "none",
            }}/>
            {/* Side fades for editorial feel */}
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to right, rgba(0,0,0,0.08) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.08) 100%)",
              pointerEvents: "none",
            }}/>
          </>
        ) : (
          /* Graceful fallback using gradient + emoji */
          <div style={{
            width: "100%", height: "100%",
            background: card.fallbackGradient,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 32,
          }}>
            {card.fallbackIcon}
          </div>
        )}
      </div>

      {/* Content — overlaps the fade */}
      <div style={{ padding: "10px 14px 14px", marginTop: -2 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: T.green, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 3, fontFamily: T.sans }}>{card.eyebrow}</div>
        <div style={{ fontFamily: T.serif, fontSize: 13.5, color: T.text, lineHeight: 1.3, marginBottom: 5, fontStyle: "italic" }}>{card.title}</div>
        <p style={{ fontSize: 11, color: T.sub, lineHeight: 1.6, margin: "0 0 7px", fontFamily: T.sans }}>{card.desc}</p>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          fontSize: 11, fontWeight: 600, color: T.green, fontFamily: T.sans,
          transform: h ? "translateX(3px)" : "translateX(0)", transition: T.tx,
        }}>
          {card.cta} <ArrowRight/>
        </div>
      </div>
    </div>
  );
}

function RightTextLink({ link, onClick, last }) {
  const [h, setH] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "8px 0", borderBottom: last ? "none" : `1px solid ${T.borderFaint}`,
        cursor: "pointer", transition: T.tx,
      }}>
      <span style={{ fontSize: 12.5, color: h ? T.green : T.textMid, fontFamily: T.sans, transition: T.tx }}>{link.label}</span>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={h ? T.green : T.ghost} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: T.tx, transform: h ? "translateX(2px)" : "none" }}>
        <path d="M9 18l6-6-6-6"/>
      </svg>
    </div>
  );
}

/* ── Comment Section (inline below video card, Instagram-style) ── */
function CommentSection({ item, open, commentCounts, setCommentCounts }) {
  const [comment, setComment] = useState("");
  const [localComments, setLocalComments] = useState(item.comments || []);

  if (!open) return null;

  const handlePost = () => {
    const text = comment.trim();
    if (!text) return;
    const newC = {
      id: Date.now(),
      user: "You",
      initials: "YO",
      avatarBg: T.greenTint,
      avatarColor: T.green,
      text,
      time: "Just now",
    };
    setLocalComments(prev => [newC, ...prev]);
    setCommentCounts(prev => ({ ...prev, [item.id]: (prev[item.id] ?? localComments.length) + 1 }));
    setComment("");
  };

  return (
    <div style={{
      borderTop: `1px solid ${T.borderFaint}`,
      background: T.bg,
      borderRadius: "0 0 20px 20px",
      overflow: "hidden",
    }}>
      {/* Comment input */}
      <div style={{
        display: "flex", gap: 10, alignItems: "center",
        padding: "14px 20px 12px",
        borderBottom: localComments.length > 0 ? `1px solid ${T.border}` : "none",
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
          background: T.greenTint, border: `2px solid ${T.greenBorder}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10.5, fontWeight: 700, color: T.green, fontFamily: T.sans,
        }}>YO</div>
        <input
          value={comment}
          onChange={e => setComment(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handlePost()}
          placeholder="Add a comment..."
          style={{
            flex: 1, height: 38, borderRadius: 999,
            border: `1.5px solid ${T.border}`, outline: "none",
            padding: "0 16px", fontSize: 13, fontFamily: T.sans,
            background: T.white, color: T.text, transition: T.tx,
          }}
          onFocus={e => e.currentTarget.style.borderColor = T.green}
          onBlur={e => e.currentTarget.style.borderColor = T.border}
        />
        <button
          onClick={handlePost}
          disabled={!comment.trim()}
          style={{
            height: 38, padding: "0 16px", borderRadius: 999,
            background: comment.trim() ? T.green : T.border,
            border: "none", color: "#fff", fontSize: 12.5, fontWeight: 600,
            cursor: comment.trim() ? "pointer" : "default",
            fontFamily: T.sans, transition: T.tx, flexShrink: 0,
          }}>
          Post
        </button>
      </div>

      {/* Comments list */}
      {localComments.length > 0 && (
        <div style={{ padding: "12px 20px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
          {localComments.map(c => (
            <div key={c.id} style={{ display: "flex", gap: 10 }}>
              <div style={{
                width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                background: c.avatarBg || T.greenTint,
                border: `2px solid ${T.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 700, color: c.avatarColor || T.green, fontFamily: T.sans,
              }}>{c.initials}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: T.text, fontFamily: T.sans }}>{c.user}</span>
                  <span style={{ fontSize: 11, color: T.ghost, fontFamily: T.sans }}>{c.time}</span>
                </div>
                <p style={{ fontSize: 13, color: T.sub, lineHeight: 1.6, margin: 0, fontFamily: T.sans }}>{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Share modal ── */
function ShareModal({ item, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/profeed?pro=${item.id}`;

  const copyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareOptions = [
    {
      label: "WhatsApp",
      color: "#25D366",
      bg: "#f0fdf4",
      icon: "💬",
      action: () => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out ${item.providerName} on JiraniApp: ${url}`)}`),
    },
    {
      label: "Copy link",
      color: T.green,
      bg: T.greenTint,
      icon: copied ? "✅" : "🔗",
      action: copyLink,
    },
    {
      label: "X / Twitter",
      color: "#000",
      bg: "#f5f5f5",
      icon: "✖",
      action: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${item.providerName} on JiraniApp`)}&url=${encodeURIComponent(url)}`),
    },
  ];

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.4)",
        zIndex: 200, backdropFilter: "blur(2px)",
      }}/>
      <div style={{
        position: "fixed", left: "50%", top: "50%",
        transform: "translate(-50%,-50%)",
        background: T.white, borderRadius: 20, padding: "28px 28px 24px",
        zIndex: 201, width: 320, boxShadow: "0 24px 64px rgba(0,0,0,.18)",
        border: `1px solid ${T.border}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontFamily: T.serif, fontSize: 18, color: T.text, fontStyle: "italic" }}>Share this video</div>
            <div style={{ fontSize: 12, color: T.faint, fontFamily: T.sans, marginTop: 2 }}>{item.providerName} · {item.category}</div>
          </div>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: "50%", border: "none",
            background: T.bg, cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          {shareOptions.map((opt, i) => (
            <button key={i} onClick={opt.action} style={{
              flex: 1, padding: "12px 8px", borderRadius: 14,
              border: `1.5px solid ${T.border}`, background: opt.bg,
              cursor: "pointer", textAlign: "center", transition: T.tx,
              fontFamily: T.sans,
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = opt.color; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <div style={{ fontSize: 20, marginBottom: 5 }}>{opt.icon}</div>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: opt.color }}>{opt.label}</div>
            </button>
          ))}
        </div>

        {/* URL bar */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8, marginTop: 16,
          padding: "10px 14px", borderRadius: 10,
          background: T.bg, border: `1.5px solid ${T.border}`,
        }}>
          <span style={{ fontSize: 11.5, color: T.faint, fontFamily: T.sans, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{url}</span>
          <button onClick={copyLink} style={{
            fontSize: 11.5, fontWeight: 600, color: T.green, background: "none",
            border: "none", cursor: "pointer", fontFamily: T.sans, flexShrink: 0,
          }}>{copied ? "Copied!" : "Copy"}</button>
        </div>
      </div>
    </>
  );
}

/* ── Video Feed Card ── */
function FeedCard({ item, isMobile, likeCounts, setLikeCounts, viewCounts, setViewCounts, commentCounts, setCommentCounts }) {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const isBP = item.tier === "Business Pro";

  const likeCount = likeCounts[item.id] ?? parseInt(String(item.likes).replace(/[^0-9.]/g, "")) * (item.likes.includes("k") ? 1000 : 1);
  const viewCount = viewCounts[item.id] ?? parseInt(String(item.views).replace(/[^0-9.]/g, "")) * (item.views.includes("k") ? 1000 : 1);
  const commentCount = commentCounts[item.id] ?? (item.comments?.length || 0);

  const formatCount = (n) => {
    if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, "") + "k";
    return String(n);
  };

  const handleVideoClick = () => {
    if (!playing) {
      // increment view on first play
      setViewCounts(prev => ({ ...prev, [item.id]: (prev[item.id] ?? viewCount) + 1 }));
    }
    setPlaying(p => !p);
  };

  const handleLike = () => {
    const current = likeCounts[item.id] ?? likeCount;
    setLikeCounts(prev => ({ ...prev, [item.id]: liked ? current - 1 : current + 1 }));
    setLiked(l => !l);
  };

  const goToBooking = () => navigate(`/pro/${item.id}?book=1`);
  const goToProfile = () => navigate(`/pro/${item.id}`);

  const bookNowStyle = {
    height: 34, padding: "0 16px", borderRadius: 9,
    background: isBP ? "#D97706" : T.green,
    border: "none", color: "#fff", fontSize: 12, fontWeight: 600,
    cursor: "pointer", fontFamily: T.sans, transition: T.tx, flexShrink: 0,
  };
  const profileStyle = {
    height: 34, padding: "0 14px", borderRadius: 9,
    border: `1.5px solid ${T.border}`, background: "transparent",
    fontSize: 12, fontWeight: 600, color: T.textMid,
    cursor: "pointer", fontFamily: T.sans, transition: T.tx,
  };

  return (
    <>
      {shareOpen && <ShareModal item={item} onClose={() => setShareOpen(false)}/>}

      <article style={{
        background: T.white, borderRadius: 20,
        border: `1.5px solid ${T.border}`,
        overflow: "hidden", marginBottom: 20,
        boxShadow: "0 2px 16px rgba(0,0,0,.05)",
        transition: T.tx,
      }}>

        {/* ── Video area ── */}
        <div
          onClick={handleVideoClick}
          style={{
            position: "relative", width: "100%",
            /* 4:5 ratio feels premium — tighter than 9:16, wider than square */
            aspectRatio: "4/5",
            maxHeight: 480,
            overflow: "hidden",
            background: imgError ? "#1a1a2e" : "#0d0d0d",
            cursor: "pointer",
          }}>
          {!imgError && (
            <img src={item.thumbnail} alt={item.providerName}
              onError={() => setImgError(true)}
              style={{
                width: "100%", height: "100%", objectFit: "cover", display: "block",
                objectPosition: "center 20%",
                transform: playing ? "scale(1.025)" : "scale(1)",
                transition: "transform .7s cubic-bezier(.4,0,.2,1)",
                filter: playing ? "brightness(0.82)" : "brightness(0.97)",
              }}
            />
          )}

          {/* Main overlay gradient — deep cinematic fade into card */}
          <div style={{
            position: "absolute", inset: 0,
            background: [
              "linear-gradient(to top,",
              "  rgba(15,17,23,0.96) 0%,",
              "  rgba(15,17,23,0.70) 14%,",
              "  rgba(15,17,23,0.22) 34%,",
              "  transparent 52%",
              ")"
            ].join(""),
            pointerEvents: "none",
          }}/>

          {/* Top vignette */}
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.28) 0%, transparent 22%)",
            pointerEvents: "none",
          }}/>

          {/* Tier badge — top left */}
          <div style={{ position: "absolute", top: 14, left: 14 }}>
            <TierBadge tier={item.tier}/>
          </div>

          {/* Category — top right */}
          <div style={{
            position: "absolute", top: 14, right: 12,
            background: "rgba(15,17,23,0.62)", backdropFilter: "blur(6px)",
            color: "rgba(255,255,255,.90)", fontSize: 10.5, fontWeight: 600,
            padding: "4px 10px", borderRadius: 6, fontFamily: T.sans, letterSpacing: ".02em",
          }}>
            {item.category}
          </div>

          {/* Play indicator */}
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%,-50%)",
            width: 52, height: 52, borderRadius: "50%",
            background: "rgba(255,255,255,.14)", border: "1.5px solid rgba(255,255,255,.36)",
            backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            opacity: playing ? 0 : 0.92, transition: "opacity .3s",
            pointerEvents: "none",
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff" stroke="none">
              <polygon points="6,3 20,12 6,21"/>
            </svg>
          </div>

          {/* Bottom info row — sits on the cinematic gradient */}
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0,
            padding: "0 16px 18px",
          }}>
            {/* Provider row inside video */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              {/* Real profile photo or initials fallback */}
              <div style={{
                width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                background: item.avatarBg,
                border: "2px solid rgba(255,255,255,0.55)",
                overflow: "hidden",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700, color: item.avatarColor, fontFamily: T.sans,
              }}>
                {item.avatarUrl ? (
                  <img src={item.avatarUrl} alt={item.providerName} style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
                ) : item.initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "#fff", fontFamily: T.sans, lineHeight: 1.2 }}>
                  {item.providerName}
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.65)", fontFamily: T.sans, display: "flex", alignItems: "center", gap: 3, marginTop: 2 }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  {item.location}
                </div>
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 4,
                background: "rgba(255,255,255,.12)", backdropFilter: "blur(6px)",
                border: "1px solid rgba(255,255,255,.2)",
                padding: "4px 9px", borderRadius: 999,
              }}>
                <StarIcon/>
                <span style={{ color: "#fff", fontSize: 11.5, fontWeight: 600, fontFamily: T.sans }}>
                  {item.rating.toFixed(1)}
                </span>
              </div>
            </div>

            {/* Stats row */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: "rgba(15,17,23,.48)", backdropFilter: "blur(6px)",
                color: "rgba(255,255,255,.85)", fontSize: 11.5, fontWeight: 500,
                padding: "4px 10px", borderRadius: 999, fontFamily: T.sans,
              }}>
                <EyeIcon/>{formatCount(viewCounts[item.id] ?? viewCount)}
              </div>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: "rgba(15,17,23,.48)", backdropFilter: "blur(6px)",
                color: "rgba(255,255,255,.85)", fontSize: 11.5, fontWeight: 500,
                padding: "4px 10px", borderRadius: 999, fontFamily: T.sans,
              }}>
                <HeartIcon filled={liked}/>{formatCount(likeCounts[item.id] ?? likeCount)}
              </div>
              <div style={{ flex: 1 }}/>
              <div style={{
                background: "rgba(15,17,23,.48)", backdropFilter: "blur(6px)",
                color: "rgba(255,255,255,.75)", fontSize: 11, fontWeight: 500,
                padding: "4px 10px", borderRadius: 999, fontFamily: T.sans,
              }}>
                {item.jobs} jobs
              </div>
            </div>
          </div>
        </div>

        {/* ── Card body — clean separation from video ── */}
        <div style={{ padding: "16px 18px 18px" }}>

          {/* Description */}
          <p style={{
            fontSize: 13.5, color: T.sub, lineHeight: 1.68,
            margin: "0 0 14px", fontFamily: T.sans,
          }}>
            {item.description}
          </p>

          {/* Action row — icons always inline; CTAs drop to their own row on mobile so nothing gets clipped */}
          <div style={{ paddingTop: 12, borderTop: `1px solid ${T.borderFaint}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: isMobile ? 10 : 0 }}>
              <ActionBtn
                icon={<HeartIcon filled={liked} size={14}/>}
                label={liked ? "Liked" : "Like"}
                active={liked}
                activeColor="#ef4444"
                onClick={handleLike}
              />
              <ActionBtn
                icon={<CommentIcon/>}
                label={`${formatCount(commentCount)}`}
                active={commentOpen}
                onClick={() => setCommentOpen(o => !o)}
              />
              <ActionBtn
                icon={<ShareIcon/>}
                label="Share"
                onClick={() => setShareOpen(true)}
              />

              {!isMobile && (
                <>
                  <div style={{ flex: 1 }}/>
                  <button onClick={goToBooking} style={bookNowStyle}
                    onMouseEnter={e => { e.currentTarget.style.background = isBP ? "#B45309" : T.greenDark; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isBP ? "#D97706" : T.green; }}
                  >Book Now</button>
                  <button onClick={goToProfile} style={{ ...profileStyle, marginLeft: 8 }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = T.greenBorder; e.currentTarget.style.background = T.greenTint; e.currentTarget.style.color = T.green; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.textMid; }}
                  >Profile</button>
                </>
              )}
            </div>

            {isMobile && (
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={goToProfile} style={{ ...profileStyle, flex: 1 }}>Profile</button>
                <button onClick={goToBooking} style={{ ...bookNowStyle, flex: 1.4 }}>Book Now</button>
              </div>
            )}
          </div>
        </div>

        {/* ── Inline comment section ── */}
        <CommentSection
          item={item}
          open={commentOpen}
          commentCounts={commentCounts}
          setCommentCounts={setCommentCounts}
        />
      </article>
    </>
  );
}

function ActionBtn({ icon, label, onClick, active = false, activeColor = T.green }) {
  const [h, setH] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "7px 10px", borderRadius: 9,
        border: "none",
        background: active ? (activeColor === "#ef4444" ? "#fff0f0" : T.greenTint) : h ? T.bg : "transparent",
        fontSize: 12.5, fontWeight: active ? 600 : 500,
        color: active ? activeColor : h ? T.green : T.sub,
        cursor: "pointer", fontFamily: T.sans, transition: T.tx,
      }}>
      {icon}{label}
    </button>
  );
}

/* ── Mobile bottom nav ── */
function MobileBottomNav({ user }) {
  const navigate = useNavigate();
  const items = [
    { icon: <HomeIcon/>, label: "Home", path: "/" },
    { icon: <SearchIcon/>, label: "Search", path: "/search" },
    { icon: <VideoIcon/>, label: "ProFeed", path: "/profeed", active: true },
    ...(user
      ? [
          { icon: <BookingsIcon/>, label: "Bookings", path: "/bookings" },
          { icon: <ProfileIcon/>, label: "Account", path: "/account" },
        ]
      : [
          { icon: <SignInIcon/>, label: "Sign In", path: "/login" },
          { icon: <SignUpIcon/>, label: "Sign Up", path: "/signup" },
        ]),
  ];
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0,
      background: T.white, borderTop: `1px solid ${T.border}`,
      display: "flex", zIndex: 50, paddingBottom: "env(safe-area-inset-bottom)",
      boxShadow: "0 -4px 20px rgba(0,0,0,.06)",
    }}>
      {items.map((item, i) => (
        <div key={i} onClick={() => navigate(item.path)} style={{
          flex: 1, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          padding: "10px 0 8px", cursor: "pointer",
          color: item.active ? T.green : T.ghost,
        }}>
          {item.icon}
          <span style={{ fontSize: 10, marginTop: 3, fontFamily: T.sans, fontWeight: item.active ? 600 : 400 }}>
            {item.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Data layer: fetch real ProFeed videos from Supabase ──────────────────────
   Shapes `pro_videos` rows (joined to `profiles` for name/avatar/category/
   location) into exactly the object shape FeedCard already expects. If your
   `pro_videos` table or its columns are named differently, adjust the
   `.select()` string and the mapper below — nothing else needs to change. */
async function fetchFeedVideos() {
  const { data, error } = await supabase
    .from("pro_videos")
    .select(`
      id, video_url, thumbnail_url, description, category,
      views_count, likes_count, jobs_count, rating, tier, created_at,
      profiles:pro_id ( full_name, avatar_url, location )
    `)
    .eq("status", "published")
    .order("created_at", { ascending: false })
    .limit(50);

  if (error) throw error;

  return (data || []).map(v => {
    const name = v.profiles?.full_name || "JiraniPro";
    const initials = name
      .split(" ")
      .filter(Boolean)
      .map(n => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

    return {
      id: v.id,
      providerName: name,
      initials: initials || "JP",
      avatarUrl: v.profiles?.avatar_url || null,
      avatarBg: T.greenTint,
      avatarColor: T.green,
      thumbnail: v.thumbnail_url || v.video_url,
      category: v.category,
      // Real per-pro town, e.g. Nairobi, Mombasa, Kisumu, Nakuru, Eldoret,
      // Thika, etc. — set by the pro in Settings → Account → Location.
      location: v.profiles?.location || "Nairobi",
      rating: typeof v.rating === "number" ? v.rating : 4.8,
      jobs: v.jobs_count ?? 0,
      tier: v.tier || "Verified Pro",
      description: v.description || "",
      likes: String(v.likes_count ?? 0),
      views: String(v.views_count ?? 0),
      comments: [],
    };
  });
}

/* ── MAIN PAGE ── */
export default function ProFeedPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeCategory, setActiveCategory] = useState("All");
  const [activeLocation, setActiveLocation] = useState("All Locations");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Shared reactive state for likes, views, comments across all cards
  const [likeCounts, setLikeCounts] = useState({});
  const [viewCounts, setViewCounts] = useState({});
  const [commentCounts, setCommentCounts] = useState({});

  // Real feed data
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    fetchFeedVideos()
      .then(items => { if (!cancelled) setFeedItems(items); })
      .catch(err => { if (!cancelled) setLoadError(err.message || "Couldn't load ProFeed videos."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filteredItems = feedItems.filter(item => {
    const catMatch = activeCategory === "All" || item.category === activeCategory;
    // Location filter: match if "All Locations" or if item.location includes the selected city
    const locMatch = activeLocation === "All Locations" || item.location.toLowerCase().includes(activeLocation.toLowerCase());
    return catMatch && locMatch;
  });

  return (
    <div style={{
      fontFamily: T.sans, background: T.bg, minHeight: "100vh",
      display: "flex", position: "relative",
    }}>
      <style>{`
        *{box-sizing:border-box}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-thumb{background:#E5E7EB;border-radius:99px}
        aside::-webkit-scrollbar{display:none}
        aside{-ms-overflow-style:none;scrollbar-width:none}
      `}</style>

      {/* Left sidebar */}
      <LeftSidebar
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        activeLocation={activeLocation}
        setActiveLocation={setActiveLocation}
        isMobile={isMobile}
        user={user}
      />

      {/* Main feed */}
      <main style={{
        flex: 1, minWidth: 0,
        padding: isMobile ? "16px 16px 80px" : "28px 32px",
        maxWidth: isMobile ? "100%" : 580,
        margin: "0 auto",
      }}>

        {/* Page header — no back button, title sits at top */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{
            fontFamily: T.serif, fontSize: isMobile ? 26 : 30,
            fontWeight: 400, color: T.text, lineHeight: 1.1,
            letterSpacing: "-0.03em", margin: "0 0 4px",
          }}>
            Trending on <em style={{ color: T.green, fontStyle: "italic" }}>ProFeed</em>
          </h1>
          <p style={{ fontSize: 13, color: T.faint, margin: 0, fontFamily: T.sans }}>
            {loading ? "Loading videos…" : `${filteredItems.length} video${filteredItems.length !== 1 ? "s" : ""} · ${activeCategory === "All" ? "All categories" : activeCategory}`}
            {!loading && activeLocation !== "All Locations" ? ` · ${activeLocation}` : ""}
          </p>
        </div>

        {/* Mobile filter strip */}
        {isMobile && (
          <div style={{ overflowX: "auto", marginBottom: 20, paddingBottom: 4 }}>
            <div style={{ display: "flex", gap: 7, width: "max-content" }}>
              {CATEGORIES.map(cat => (
                <button key={cat}
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    height: 32, padding: "0 13px", borderRadius: 999,
                    border: `1.5px solid ${activeCategory === cat ? T.text : T.border}`,
                    background: activeCategory === cat ? T.text : T.white,
                    color: activeCategory === cat ? "#fff" : T.sub,
                    fontSize: 12, fontWeight: activeCategory === cat ? 600 : 400,
                    fontFamily: T.sans, cursor: "pointer", whiteSpace: "nowrap",
                  }}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {[0, 1].map(i => (
              <div key={i} style={{
                background: T.white, borderRadius: 20, border: `1.5px solid ${T.border}`,
                aspectRatio: "4/5", maxHeight: 480,
                backgroundImage: `linear-gradient(90deg, ${T.borderFaint} 25%, ${T.bg} 37%, ${T.borderFaint} 63%)`,
                backgroundSize: "400% 100%",
                animation: "pfp-shimmer 1.4s ease infinite",
              }}/>
            ))}
            <style>{`@keyframes pfp-shimmer{0%{background-position:100% 50%}100%{background-position:0 50%}}`}</style>
          </div>
        )}

        {/* Error state */}
        {!loading && loadError && (
          <div style={{
            textAlign: "center", padding: "60px 24px",
            background: T.white, borderRadius: 20, border: `1.5px solid ${T.border}`,
          }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <p style={{ fontFamily: T.serif, fontSize: 20, color: T.text, fontStyle: "italic", margin: "0 0 8px" }}>
              Couldn't load ProFeed
            </p>
            <p style={{ fontSize: 14, color: T.sub, fontFamily: T.sans, margin: 0, lineHeight: 1.6 }}>
              {loadError}
            </p>
          </div>
        )}

        {/* Feed */}
        {!loading && !loadError && (
          filteredItems.length > 0 ? (
            filteredItems.map(item => (
              <FeedCard
                key={item.id}
                item={item}
                isMobile={isMobile}
                likeCounts={likeCounts}
                setLikeCounts={setLikeCounts}
                viewCounts={viewCounts}
                setViewCounts={setViewCounts}
                commentCounts={commentCounts}
                setCommentCounts={setCommentCounts}
              />
            ))
          ) : (
            <div style={{
              textAlign: "center", padding: "60px 24px",
              background: T.white, borderRadius: 20, border: `1.5px solid ${T.border}`,
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🎬</div>
              <p style={{ fontFamily: T.serif, fontSize: 20, color: T.text, fontStyle: "italic", margin: "0 0 8px" }}>
                No videos yet
              </p>
              <p style={{ fontSize: 14, color: T.sub, fontFamily: T.sans, margin: "0 0 20px", lineHeight: 1.6 }}>
                No {activeCategory !== "All" ? activeCategory : ""} professionals
                {activeLocation !== "All Locations" ? ` in ${activeLocation}` : ""} have posted to ProFeed yet.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                {activeCategory !== "All" && (
                  <button onClick={() => setActiveCategory("All")} style={{
                    fontSize: 13, fontWeight: 600, color: T.green,
                    background: T.greenTint, border: `1.5px solid ${T.greenBorder}`,
                    padding: "10px 20px", borderRadius: 999, cursor: "pointer", fontFamily: T.sans,
                  }}>All categories</button>
                )}
                {activeLocation !== "All Locations" && (
                  <button onClick={() => setActiveLocation("All Locations")} style={{
                    fontSize: 13, fontWeight: 600, color: T.textMid,
                    background: T.bg, border: `1.5px solid ${T.border}`,
                    padding: "10px 20px", borderRadius: 999, cursor: "pointer", fontFamily: T.sans,
                  }}>All locations</button>
                )}
              </div>
            </div>
          )
        )}
      </main>

      {/* Right sidebar */}
      <RightSidebar isMobile={isMobile} />

      {/* Mobile bottom nav */}
      {isMobile && <MobileBottomNav user={user}/>}
    </div>
  );
}